import type {
  DocumentTypeDetailModel,
  DocumentTypeDetailViewModel,
  DocumentTypeInfoModel,
  DocumentTypeInfoViewModel,
  DocumentTypeResponse,
  DocumentTypeViewModel,
  UpdateDocumentTypeRequest,
  FlowVersionSummaryResponse,
  FlowVersionSummaryViewModel,
  FlowConfigResponse,
  FlowConfigViewModel,
  FlowStepResponse,
  FlowStepViewModel,
  LLMFlowStepConfigResponse,
  LLMFlowStepConfigViewModel,
  OCRFlowStepConfigResponse,
  OCRFlowStepConfigViewModel,
  AgentExecutionResponse,
  AgentExecutionViewModel,
  CreateFlowConfigRequest,
  ActivateFlowRequest,
  LLMConfigOptionsViewModel,
  LLMConfigOptions,
} from "@models/doctype";

// ============================================================================
// Request Builders (camelCase to snake_case)
// ============================================================================

export const buildUpdateDocumentTypeRequest = (
  data: UpdateDocumentTypeRequest,
) => {
  const request: Record<string, unknown> = {};
  if (data.docTypeCode !== undefined) request.doctype_code = data.docTypeCode;
  if (data.docTypeName !== undefined) request.doctype_name = data.docTypeName;
  if (data.description !== undefined) request.description = data.description;
  if (data.status !== undefined) request.status = data.status;
  return request;
};

export const buildCreateFlowConfigRequest = (data: CreateFlowConfigRequest) => {
  return {
    flow_base_id: data.flow_base_id,
    flow_type: data.flow_type,
    flow_description: data.flow_description,
    steps: data.steps,
  };
};

export const buildActivateFlowRequest = (data: ActivateFlowRequest) => {
  return {
    flow_base_id: data.flow_base_id,
    version: data.version,
  };
};

// ============================================================================
// Response Builders (snake_case to camelCase)
// ============================================================================

export const buildDocumentTypesInfoViewModel = (
  response: DocumentTypeInfoModel[],
): DocumentTypeInfoViewModel[] => {
  return response.map((doctype) => ({
    docTypeCode: doctype.doctype_code,
    docTypeName: doctype.doctype_name,
    logoUrl: doctype.logo_url ?? null,
    description: doctype.description ?? null,
    categoryId: doctype.category_id ?? null,
    categoryName: doctype.category_name ?? null,
    categoryDescription: doctype.category_description ?? null,
    status: doctype.status,
  }));
};

export const buildSingleDocumentTypeDetailViewModel = (
  doctype: DocumentTypeDetailModel,
): DocumentTypeDetailViewModel => ({
  id: doctype.id,
  docTypeCode: doctype.doctype_code,
  docTypeName: doctype.doctype_name,
  logoUrl: doctype.logo_url ?? null,
  description: doctype.description,
  categoryId: doctype.category_id ?? null,
  categoryName: doctype.category_name ?? null,
  organizationId: doctype.organization_id ?? null,
  status: doctype.status,
  mappingConfig: doctype.mapping_config
    ? {
        enabled: doctype.mapping_config.enabled,
        service: doctype.mapping_config.service ?? null,
        pluginId: doctype.mapping_config.plugin_id ?? null,
      }
    : null,
  createdAt: doctype.created_at ? new Date(doctype.created_at) : null,
  updatedAt: doctype.updated_at ? new Date(doctype.updated_at) : null,
});

export const buildDocumentTypeDetailViewModel = (
  response: DocumentTypeDetailModel[],
): DocumentTypeDetailViewModel[] => {
  return response.map(buildSingleDocumentTypeDetailViewModel);
};

export const buildDocumentTypeViewModel = (
  response: DocumentTypeResponse,
): DocumentTypeViewModel => {
  return {
    id: response.id,
    docTypeCode: response.doctype_code,
    docTypeName: response.doctype_name,
    description: response.description,
    status: response.status,
    createdAt: response.created_at ? new Date(response.created_at) : null,
    updatedAt: response.updated_at ? new Date(response.updated_at) : null,
  };
};

// ============================================================================
// Flow Configuration Builders
// ============================================================================
export const buildLLMConfigOptionsViewModel = (
  response: LLMConfigOptions,
): LLMConfigOptionsViewModel => {
  return {
    temperature: response.temperature,
    topP: response.top_p,
    topK: response.top_k,
    maxOutputTokens: response.max_output_tokens,
    thinkingBudget: response.thinking_budget,
    seed: response.seed,
  };
};

export const buildLLMFlowStepConfigViewModel = (
  response: LLMFlowStepConfigResponse,
): LLMFlowStepConfigViewModel => {
  return {
    provider: response.provider,
    systemPrompt: response.system_prompt,
    userPrompt: response.user_prompt,
    modelName: response.model_name,
    configOptions: response.config_options
      ? buildLLMConfigOptionsViewModel(response.config_options)
      : null,
    responseSchema: response.response_schema,
    executions: response.executions
      ? response.executions.map(buildAgentExecutionViewModel)
      : null,
  };
};

export const buildOCRFlowStepConfigViewModel = (
  response: OCRFlowStepConfigResponse,
): OCRFlowStepConfigViewModel => {
  return {
    provider: response.provider,
    modelName: response.model_name,
    ocrOptions: response.ocr_options,
  };
};

export const buildFlowStepViewModel = (
  response: FlowStepResponse,
): FlowStepViewModel => {
  return {
    stepOrder: response.step_order,
    stepType: response.step_type,
    llmStepConfig: response.llm_step_config
      ? buildLLMFlowStepConfigViewModel(response.llm_step_config)
      : undefined,
    ocrStepConfig: response.ocr_step_config
      ? buildOCRFlowStepConfigViewModel(response.ocr_step_config)
      : undefined,
  };
};

export const buildAgentExecutionViewModel = (
  response: AgentExecutionResponse,
): AgentExecutionViewModel => {
  return {
    domainKey: response.domain_key,
    userPrompt: response.user_prompt,
    responseSchema: response.response_schema,
    systemPrompt: response.system_prompt ?? null,
    modelName: response.model_name ?? null,
    configOptions: response.config_options
      ? buildLLMConfigOptionsViewModel(response.config_options)
      : null,
  };
};

export const buildFlowVersionSummaryViewModel = (
  response: FlowVersionSummaryResponse,
): FlowVersionSummaryViewModel => {
  return {
    id: response.id,
    flowBaseId: response.flow_base_id,
    version: response.version,
    isActive: response.is_active,
    flowType: response.flow_type,
    createdAt: response.created_at ? new Date(response.created_at) : null,
    updatedAt: response.updated_at ? new Date(response.updated_at) : null,
  };
};

export const buildFlowVersionSummariesViewModel = (
  responses: FlowVersionSummaryResponse[],
): FlowVersionSummaryViewModel[] => {
  return responses
    .sort((a, b) => b.version - a.version)
    .map(buildFlowVersionSummaryViewModel);
};

export const buildFlowConfigViewModel = (
  response: FlowConfigResponse,
): FlowConfigViewModel => {
  return {
    id: response.id,
    flowBaseId: response.flow_base_id,
    docTypeId: response.doctype_id,
    flowType: response.flow_type,
    flowDescription: response.flow_description,
    version: response.version,
    steps: response.steps.map(buildFlowStepViewModel),
    isActive: response.is_active,
    updatedBy: response.updated_by,
    createdAt: response.created_at ? new Date(response.created_at) : null,
    updatedAt: response.updated_at ? new Date(response.updated_at) : null,
  };
};
